// Jobs at a place (P36b): what a plazmi on the Commons, the playa or in the
// wilds keeps doing, as a loop of actions (actions/places.ts) played by its
// Life: chop, carry the log to the pile, set it down, walk back, chop again.
// The pages decide who does what from their public data (who's gathering what,
// whose stall it is, who's raising which build, who turned the windmill), and
// this keeps each one at it: when a step ends it starts the next, and a step
// that something else cut short (an emote, a dance) is simply picked up again.
// An emoji pops over its head as each round begins, so the work reads from far off.
import * as THREE from 'three';
import type { Life } from './life.js';
import type { Fx } from './fx.js';

export type P = [number, number];
// together: a step the whole crew does at once (a beam raised together): whoever's ready first keeps
// at `fill` (a few more taps of the hammer) till the rest are, or 15 s have gone by
export type Step = { action: string; secs: number; at?: P; face?: P; carry?: string | null; label: string; emoji?: string; together?: string; fill?: Step };
type Job = { key: string; steps: Step[]; i: number; started: boolean; issued: number; once: Step[]; wait?: string | null; waitSince?: number };

const UP = new THREE.Vector3(0, 0.6, 0);

export class Jobs {
  private jobs = new Map<Life, Job>();
  private born = performance.now();
  // whoever's at work when the page opens is already at it, not walking over from the gate
  private snap = (life: Life) => !this.jobs.has(life) && performance.now() - this.born < 12000;
  constructor(private fx: Fx, private headOf: (l: Life) => THREE.Vector3) {}

  // keep `life` at these steps, over and over (the same key: carry on where it is)
  set(life: Life, key: string, steps: Step[]) {
    const j = this.jobs.get(life);
    if (j && j.key === key) return;
    const snap = this.snap(life);
    this.jobs.set(life, { key, steps, i: 0, started: false, issued: 0, once: j?.once || [] });
    this.issue(life, snap);
  }
  // a step once, before going back to the job (a sale, a chip-in, a plank for the effigy)
  once(life: Life, steps: Step[]) {
    const j = this.jobs.get(life) || { key: '', steps: [], i: 0, started: false, issued: 0, once: [] };
    j.once.push(...steps); this.jobs.set(life, j);
    this.issue(life);
  }
  clear(life: Life) { this.jobs.delete(life); }
  has(life: Life) { return this.jobs.has(life); }
  // drop the jobs of anyone not in `keep` (a one-off still to come stays)
  keepOnly(keep: Set<Life>) { for (const [l, j] of [...this.jobs]) if (!keep.has(l) && !j.once.length) this.jobs.delete(l); }

  private cur(j: Job) { return j.once[0] || j.steps[j.i % Math.max(1, j.steps.length)]; }
  private issue(life: Life, snap = false, step?: Step) {
    const j = this.jobs.get(life)!, s = step || this.cur(j);
    if (!s) return;
    j.started = false; j.issued = performance.now();
    life.work(s.action, s.secs, s.label, { at: s.at, face: s.face, carry: s.carry ?? null, snap });
    if (s.emoji && life.root.visible) this.fx.emoji(this.headOf(life).add(UP), s.emoji, 0.45, 1.6);
  }

  // everyone whose job has this together-step is waiting at it
  private ready(key: string) {
    for (const j of this.jobs.values()) if (j.steps.some((s) => s.together === key) && j.wait !== key) return false;
    return true;
  }
  private release(key: string) { for (const [l, j] of this.jobs) if (j.wait === key) { j.wait = null; this.issue(l); } }

  update() {
    const now = performance.now();
    for (const [life, j] of this.jobs) {
      if (life.gone) { this.jobs.delete(life); continue; }
      if (j.wait) {
        const f = this.cur(j).fill;
        if (this.ready(j.wait) || now - (j.waitSince || 0) > 15000) this.release(j.wait);
        else if (f && life.working !== f.action && now - j.issued > 1500) this.issue(life, false, f);
        continue;
      }
      const s = this.cur(j);
      if (!s) continue;
      if (life.working === s.action) { j.started = true; continue; }
      // finished (or cut short): on to the next; never started in a while: ask again
      if (j.started) {
        if (j.once.length) j.once.shift(); else j.i++;
        const n = this.cur(j);
        if (!j.once.length && n?.together) { j.wait = n.together; j.waitSince = now; j.started = false; if (n.fill) this.issue(life, false, n.fill); continue; }
        this.issue(life);
      } else if (now - j.issued > 30000) this.issue(life);
    }
  }
}
